import axios, { AxiosError } from 'axios';
import type { AxiosInstance } from 'axios';
import {
  getCookie,
  setCookie,
  clearListCookies,
} from '../shared/helpers/cookies';

export type Env = any;
export type Api = AxiosInstance;
export type ErrorHandler = any;

type RefreshScope = 'primary' | 'adult';

type RefreshQueueItem = {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
};

type RefreshState = {
  isRefreshing: boolean;
  queue: RefreshQueueItem[];
};

type ExtendedRequestConfig = {
  _retry?: boolean;
  url?: string;
  params?: Record<string, any>;
  data?: any;
  headers?: Record<string, any>;
};

const api: Api = axios.create({
  baseURL: '/api',
});

const refreshStates: Record<RefreshScope, RefreshState> = {
  primary: { isRefreshing: false, queue: [] },
  adult: { isRefreshing: false, queue: [] },
};

const refreshSkipPaths = [
  '/api/v1/amos/clients/login',
  '/api/v1/amos/clients/login-adult',
  '/api/v1/amos/clients/login-b2b',
  '/api/v1/amos/clients/login-b2b-adult',
  '/api/v1/amos/clients/token-exchange',
  '/api/v1/amos/clients/refresh-token',
  '/api/v1/amos/clients/logout',
  '/api/v1/amos/clients/b2c-logout',
];

const shouldSkipRefresh = (url = '') => {
  return refreshSkipPaths.some((path) => url.includes(path));
};

const extractClientTokenFromUrl = (url = '') => {
  const match = url.match(/[?&]clientToken=([^&]+)/);
  return match ? decodeURIComponent(match[1]) : null;
};

const extractClientTokenFromRequest = (config: ExtendedRequestConfig) => {
  if (config.params?.clientToken) return String(config.params.clientToken);

  if (
    config.data &&
    typeof config.data === 'object' &&
    config.data.clientToken
  ) {
    return String(config.data.clientToken);
  }

  if (typeof config.data === 'string' && config.data.trim().length > 0) {
    return config.data;
  }

  return extractClientTokenFromUrl(config.url || '');
};

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const replaceTokenInUrl = (
  url: string,
  newToken: string,
  oldToken?: string | null
) => {
  if (!url.includes('clientToken=')) return url;

  if (oldToken) {
    const encodedOld = escapeRegExp(encodeURIComponent(oldToken));
    return url.replace(
      new RegExp(`(clientToken=)${encodedOld}`),
      `$1${encodeURIComponent(newToken)}`
    );
  }

  return url.replace(
    /(clientToken=)[^&]+/,
    `$1${encodeURIComponent(newToken)}`
  );
};

const replaceTokenInRequest = (
  config: ExtendedRequestConfig,
  newToken: string,
  oldToken?: string | null
) => {
  if (config.url) {
    config.url = replaceTokenInUrl(config.url, newToken, oldToken);
  }

  if (config.params?.clientToken) {
    config.params.clientToken = newToken;
  }

  if (
    config.data &&
    typeof config.data === 'object' &&
    config.data.clientToken
  ) {
    config.data.clientToken = newToken;
  }

  if (typeof config.data === 'string' && oldToken && config.data === oldToken) {
    config.data = newToken;
  }
};

const isInvalidTokenError = (error: AxiosError<any>) => {
  const status = error?.response?.status;
  const message = String(error?.response?.data?.message || '').toLowerCase();
  const errorCode = String(
    error?.response?.data?.error_code || ''
  ).toLowerCase();

  if (status === 401) return true;

  if (
    (status === 500 || status === 400) &&
    (message.includes('140 - invalid token') ||
      message.includes('invalid token') ||
      errorCode === 'invalid_token')
  ) {
    return true;
  }

  return false;
};

const flushQueue = (scope: RefreshScope, error: unknown, newToken?: string) => {
  const state = refreshStates[scope];

  state.queue.forEach((item) => {
    if (error) {
      item.reject(error);
    } else if (newToken) {
      item.resolve(newToken);
    } else {
      item.reject(new Error('Token refresh failed'));
    }
  });

  state.queue = [];
};

const persistTokens = (
  scope: RefreshScope,
  payload: {
    clientToken: string;
    clientRefreshToken: string;
    clientId?: string;
  }
) => {
  if (scope === 'adult') {
    setCookie('amosAdultClientToken', payload.clientToken);
    setCookie('amosAdultRefreshToken', payload.clientRefreshToken);
    if (payload.clientId) setCookie('amosAdultClientId', payload.clientId);
  } else {
    setCookie('amosToken', payload.clientToken);
    setCookie('amosRefreshToken', payload.clientRefreshToken);
    if (payload.clientId) setCookie('amosClient', payload.clientId);
  }
};

const getScopeAndRefreshToken = (failedClientToken: string | null) => {
  const adultToken = getCookie('amosAdultClientToken');
  const scope: RefreshScope =
    failedClientToken && adultToken && failedClientToken === adultToken
      ? 'adult'
      : 'primary';

  const refreshToken =
    scope === 'adult'
      ? getCookie('amosAdultRefreshToken')
      : getCookie('amosRefreshToken');

  return { scope, refreshToken };
};

const hardLogout = () => {
  try {
    sessionStorage.setItem('isLoggingOut', 'true');
  } catch (e) {
    // ignore
  }
  clearListCookies();
  window.location.href = '/logoff';
};

const callRefreshEndpoint = async (refreshToken: string) => {
  const isGenericDomain =
    window.location.hostname === import.meta.env.VITE_DOMAIN_GENERIC;
  const url = isGenericDomain
    ? '/api/api/v1/amos/clients/b2c-refresh-token'
    : '/api/api/v1/amos/clients/refresh-token';
  const response = await axios.post(
    url,
    { clientRefreshToken: refreshToken },
    {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Pragma: 'no-cache',
        'Cache-Control': 'no-cache',
        locale: getCookie('idiom'),
      },
    }
  );
  return response.data;
};

api.interceptors.request.use(
  function (config: any) {
    if (config.url && config.url.startsWith('/api')) {
      config.headers = config.headers || {};
      config.headers.Pragma = 'no-cache';
      config.headers['Cache-Control'] = 'no-cache';
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.headers.locale = getCookie('idiom');
    }
    return config;
  },
  function (err) {
    return Promise.reject(err);
  }
);

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<any>) => {
    const originalRequest = (error.config || {}) as ExtendedRequestConfig;
    const requestUrl = String(originalRequest.url || '');

    if (!originalRequest || shouldSkipRefresh(requestUrl)) {
      return Promise.reject(error);
    }

    if (!isInvalidTokenError(error)) {
      return Promise.reject(error);
    }

    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    const failedClientToken = extractClientTokenFromRequest(originalRequest);
    const { scope, refreshToken } = getScopeAndRefreshToken(failedClientToken);
    const state = refreshStates[scope];

    if (!refreshToken) {
      hardLogout();
      return Promise.reject(error);
    }

    if (state.isRefreshing) {
      return new Promise((resolve, reject) => {
        state.queue.push({
          resolve: (newToken) => {
            originalRequest._retry = true;
            replaceTokenInRequest(originalRequest, newToken, failedClientToken);
            resolve(api(originalRequest as any));
          },
          reject,
        });
      });
    }

    originalRequest._retry = true;
    state.isRefreshing = true;

    try {
      const refreshed = await callRefreshEndpoint(refreshToken);

      console.log('refreshed', refreshed);
      if (!refreshed?.clientToken || !refreshed?.clientRefreshToken) {
        throw new Error('Refresh response missing required token fields');
      }

      persistTokens(scope, {
        clientToken: refreshed.clientToken,
        clientRefreshToken: refreshed.clientRefreshToken,
        clientId: refreshed.clientId,
      });

      flushQueue(scope, null, refreshed.clientToken);

      replaceTokenInRequest(
        originalRequest,
        refreshed.clientToken,
        failedClientToken
      );

      return api(originalRequest as any);
    } catch (refreshError) {
      flushQueue(scope, refreshError);
      hardLogout();
      return Promise.reject(refreshError);
    } finally {
      state.isRefreshing = false;
    }
  }
);

export default api;
